import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth, useVeEquipa } from '@/features/auth/AuthContext';
import { downloadMapaMensalPdf, useMapaMensal } from '@/features/mapas/hooks';
import { formatDiaCurto, formatHoras, monthOf, pct, todayISO } from '@/lib/format';
import type { Language } from '@/i18n';
import { AppLayout } from '@/components/AppLayout';
import { MonthNavigator } from '@/components/MonthNavigator';
import { BarrasHorizontais } from '@/components/charts/BarrasHorizontais';
import { StatTile } from '@/components/charts/StatTile';
import { EmptyState } from '@/components/ui/EmptyState';
import { Icon } from '@/components/ui/Icon';
import { useToast } from '@/components/ui/Toast';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Spinner } from '@/components/ui/Spinner';

/**
 * "Mapa mensal de consulta": the month rolled up by obra and day. Scope follows
 * the role — an eletricista sees only their own hours, chefia sees the team's.
 */
export function MapaPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'pt') as Language;
  const navigate = useNavigate();
  const { user } = useAuth();
  const veEquipa = useVeEquipa();
  const toast = useToast();
  const [mes, setMes] = useState(monthOf(todayISO()));

  const mapa = useMapaMensal(mes);

  return (
    <AppLayout
      title={t('header.mapaMensal')}
      subtitle={veEquipa ? t('header.todasHoras') : (user?.displayName ?? '')}
    >
      <div className="flex flex-col gap-4">
        <div className="md:max-w-sm">
          <MonthNavigator
          mes={mes}
          onChange={setMes}
            scope={veEquipa ? t('mapa.equipa') : (user?.displayName.split(' ')[0] ?? '')}
          />
        </div>

        {mapa.isLoading && (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        )}
        {mapa.error && <ErrorBanner error={mapa.error} onRetry={() => void mapa.refetch()} />}

        {mapa.data && (
          <>
            <div className="grid grid-cols-2 gap-2 md:gap-4 lg:max-w-2xl">
              <StatTile
                tone="navy"
                label={t('mapa.horasNoMes')}
                value={formatHoras(mapa.data.totalHoras)}
                sub={t('mapa.cumprimento', { pct: pct(mapa.data.totalHoras, mapa.data.horasAlvo) })}
                progress={pct(mapa.data.totalHoras, mapa.data.horasAlvo)}
              />
              <StatTile
                label={t('mapa.diasObras')}
                value={`${mapa.data.diasComRegisto} / ${mapa.data.obrasDistintas}`}
                sub={t('mapa.vsAlvo', { horas: formatHoras(mapa.data.horasAlvo) })}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                void (async () => {
                  try {
                    await downloadMapaMensalPdf(mes);
                    toast.show(t('equipa.exportadoPdf'));
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : t('common.error'));
                  }
                })();
              }}
              className="btn-ghost w-full sm:w-auto sm:self-start sm:px-6"
            >
              <Icon name="picture_as_pdf" className="text-[18px]" /> {t('mapa.imprimirPdf')}
            </button>

            {mapa.data.porObra.length === 0 ? (
              <EmptyState message={t('mapa.semDados')} />
            ) : (
              <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
                <div className="flex flex-col gap-2">
                <div className="label-eyebrow">{t('mapa.distribuicaoObra')}</div>
                <div className="card px-4 pt-4">
                  <BarrasHorizontais
                    items={mapa.data.porObra.map((o) => ({
                      id: o.obraId,
                      label: o.nome,
                      sublabel: o.centroCusto,
                      valor: o.horas,
                      valorLabel: formatHoras(o.horas),
                    }))}
                  />
                </div>
                </div>

                <div className="flex flex-col gap-2">
                <div className="label-eyebrow">{t('mapa.diaADia')}</div>
                <ul className="card divide-y divide-[#f0f1f6] overflow-hidden">
                  {[...mapa.data.porDia].reverse().map((dia) => (
                    <li key={dia.data}>
                      <button
                        type="button"
                        onClick={() => navigate(`/hoje?data=${dia.data}`)}
                        className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                      >
                        <span className="flex min-w-0 flex-col gap-0.5">
                          <span className="text-[13px] font-semibold text-conduril-ink">
                            {formatDiaCurto(dia.data, lang)}
                          </span>
                          <span className="text-[11px] leading-snug text-conduril-muted">
                            {t('mapa.detalheDia', { obras: dia.obras, pessoas: dia.pessoas })}
                          </span>
                        </span>
                        <span className="num shrink-0 text-[14px] font-semibold text-conduril">
                          {formatHoras(dia.horas)}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
